
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { Link } from 'react-router-dom';

export default function About() {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl font-bold text-slate-800 mb-6">About Elbert Alexander</h1>
              <p className="text-xl text-slate-600 mb-8">
                Multi-genre author, creative entrepreneur, and passionate advocate for personal growth 
                through purposeful reading and reflection.
              </p>
              <div className="flex gap-4">
                <Link to="/shop">
                  <Button>Explore My Books</Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline">Get in Touch</Button>
                </Link>
              </div>
            </div>
            <div className="flex justify-center">
              <img 
                src="https://static.readdy.ai/image/b75c0c2a711814bf71b853c42d7775ec/e586cc191582f260616d9651a146ebba.jfif"
                alt="Elbert Alexander"
                className="w-96 h-[480px] object-cover object-center rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Biography */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-800 mb-8 text-center">My Journey</h2>
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-slate-600 mb-6">
              Elbert Alexander stands out as an entrepreneur, technologist, coach, and author recognized for his ability to integrate innovation with personal growth. Drawing upon his extensive experience in both technology and business, Elbert empowers individuals to achieve their goals through effective coaching and strategic guidance.
            </p>
            <p className="text-lg text-slate-600 mb-6">
              As a coach, Elbert has helped people navigate personal and professional growth. He addresses a wide range of individual needs, fosters resilience, and encourages clear goal setting. His approach ensures that those he works with can attain lasting success by focusing on both immediate objectives and long-term development.
            </p>
            <p className="text-lg text-slate-600 mb-6">
              Elbert's main work, "The Dream Journal", is a practical guide that aims to help readers find their purpose and create achievable goals. With thoughtful prompts, it encourages self-reflection, clarifies objectives, and lays out an organized path for personal development. The journal serves as a space where creative ideas are put into action, enabling users to track their thoughts, set clear intentions, and stay accountable as they work towards their dreams.
            </p>
            <p className="text-lg text-slate-600 mb-6">
              Elbert's portfolio features a journal, workshops, speaking events, and online courses, all designed for practical self-improvement. He blends strategy with spiritual insight to help people achieve lasting personal and professional growth.
            </p>
            <p className="text-lg text-slate-600 mb-6">
              Elbert combines expertise in technology and business with spiritual wisdom, encouraging intentional growth and purposeful action. His philosophy informs his work, especially in "The Dream Journal", where he helps readers clarify their purpose, note insights, and pursue goals. He integrates innovative thinking with spiritual understanding to inspire meaningful change.
            </p>
            <p className="text-lg text-slate-600">
              Elbert inspires others through workshops, speaking events, and online training to rediscover purpose and achieve meaningful growth. He believes success is about positively impacting others and communities, not just personal achievements. Elbert motivates people to start ventures, pursue spiritual growth, and create change aligned with their core values.
            </p>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
              Making an Impact
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Through books, workshops, and speaking engagements, Elbert has touched countless lives
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-slate-800 text-white rounded-full mx-auto mb-4">
                <i className="ri-book-open-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-2">Published Author</h3>
              <p className="text-slate-600">
                Created transformative resources including "The Dream Journal" that guide readers toward purposeful living.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-slate-800 text-white rounded-full mx-auto mb-4">
                <i className="ri-presentation-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-2">Dynamic Speaker</h3>
              <p className="text-slate-600">
                Delivers powerful presentations that inspire audiences to pursue their dreams and achieve meaningful growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <blockquote className="text-xl md:text-2xl font-bold text-slate-800 mb-6 italic">
            "Dreams become real through discipline and faith."
          </blockquote>
          <p className="text-xl text-slate-600">— Elbert Alexander</p>
        </div>
      </section>

      {/* Achievements */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-800 mb-12 text-center">Achievements & Recognition</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-slate-800 text-white rounded-full mx-auto mb-4">
                <i className="ri-book-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-2">Transformative Book</h3>
              <p className="text-slate-600">
                Created "The Dream Journal," a powerful tool that has helped thousands discover their purpose and achieve their goals.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-slate-800 text-white rounded-full mx-auto mb-4">
                <i className="ri-award-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-2">Multi-Faceted Expertise</h3>
              <p className="text-slate-600">
                Combines entrepreneurship, technology, coaching, and writing to provide comprehensive transformation guidance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Writing Philosophy */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-800 mb-8 text-center">Writing Philosophy</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-slate-50 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-slate-800 mb-4">Purpose-Driven Content</h3>
              <p className="text-slate-600">
                Every book is written with the intention to create real, lasting change in readers' lives. 
                I believe in practical wisdom that can be immediately applied.
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-slate-800 mb-4">Faith-Centered Approach</h3>
              <p className="text-slate-600">
                My faith informs everything I write, providing a foundation of hope, purpose, and 
                moral clarity that resonates with readers seeking deeper meaning.
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-slate-800 mb-4">Accessible Wisdom</h3>
              <p className="text-slate-600">
                Complex concepts are broken down into digestible, actionable steps that anyone can 
                follow, regardless of their background or experience level.
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-slate-800 mb-4">Community Building</h3>
              <p className="text-slate-600">
                Beyond individual transformation, I aim to build communities of like-minded individuals 
                who support each other's growth and success.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-slate-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Begin Your Transformation?</h2>
          <p className="text-xl mb-8">
            Join thousands of readers who have discovered their purpose and unlocked their potential 
            through intentional growth and faith-based wisdom.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button 
                size="lg" 
                className="bg-yellow-500 text-black hover:bg-yellow-400 font-semibold"
              >
                Start Your Growth Journey →
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
